import React, { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wrench, FileWarning, RefreshCw, CheckCircle2, AlertCircle, Loader2, ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const KAT_LABEL = {
  BG: "Bildungsgutschein", DS: "Datenschutz", EG: "Erstgespräch",
  MV: "Maßnahmevertrag", AL: "Anwesenheitsliste", ZE: "Zertifikat",
  PV: "Praktikumsvertrag", LZ: "Praktikums-Laufzettel", FZ: "Führungszeugnis", KB: "Klassenbuch",
  SP: "Stundenplan", RE: "Rechnung", SO: "Sonstige",
};

const AnhangRepair = ({ open, onOpenChange }) => {
  const [scanning, setScanning] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [deleteInlineAfter, setDeleteInlineAfter] = useState(false);

  const scan = useCallback(async () => {
    setScanning(true);
    setDryRunResult(null);
    const t = toast.loading("Scanne alle Teilnehmer-Anhänge...");
    try {
      const res = await axios.post(`${API}/azav/anhang-repair`, { dry_run: true });
      setDryRunResult(res.data);
      const s = res.data.statistik || {};
      const willMigrate = (s.inline_anhaenge_gefunden - s.inline_dedupliziert)
                         + (s.pseudo_anhaenge_gefunden - s.pseudo_dedupliziert);
      toast.success(`Scan fertig: ${s.tn_gescannt} TN, ${willMigrate} Anhänge zu migrieren`, { id: t });
    } catch (e) {
      toast.error("Scan fehlgeschlagen: " + (e.response?.data?.detail || e.message), { id: t });
    } finally {
      setScanning(false);
    }
  }, []);

  const migrate = useCallback(async () => {
    if (!dryRunResult) return;
    const s = dryRunResult.statistik || {};
    const willMigrate = (s.inline_anhaenge_gefunden - s.inline_dedupliziert)
                       + (s.pseudo_anhaenge_gefunden - s.pseudo_dedupliziert);
    const msg = `${willMigrate} Anhänge in die saubere Collection migrieren?` +
                (deleteInlineAfter ? "\n\nACHTUNG: Inline-Arrays werden NACH der Migration geleert!" : "");
    if (!window.confirm(msg)) return;

    setMigrating(true);
    const t = toast.loading("Migriere Anhänge...");
    try {
      const res = await axios.post(`${API}/azav/anhang-repair`, {
        dry_run: false,
        delete_inline_after: deleteInlineAfter,
      });
      const st = res.data.statistik || {};
      toast.success(
        `${st.inline_migriert + st.pseudo_migriert} Anhänge migriert, ` +
        `${st.kategorie_korrigiert} Kategorien korrigiert (${st.tn_betroffen}/${st.tn_gescannt} TN betroffen)`,
        { id: t, duration: 8000 }
      );
      // Neuen Scan anwerfen damit man sieht dass alles weg ist
      setTimeout(scan, 500);
    } catch (e) {
      toast.error("Migration fehlgeschlagen: " + (e.response?.data?.detail || e.message), { id: t });
    } finally {
      setMigrating(false);
    }
  }, [dryRunResult, deleteInlineAfter, scan]);

  const stats = dryRunResult?.statistik || {};
  const aktionen = dryRunResult?.aktionen || [];
  const willMigrate = (stats.inline_anhaenge_gefunden || 0) - (stats.inline_dedupliziert || 0)
                     + (stats.pseudo_anhaenge_gefunden || 0) - (stats.pseudo_dedupliziert || 0);
  const migrateAktionen = aktionen.filter(a => a.typ === "inline_migrate" || a.typ === "pseudo_migrate");

  const [restoreScanResult, setRestoreScanResult] = useState(null);
  const [restoringCat, setRestoringCat] = useState(null);

  const scanDeleted = useCallback(async () => {
    const t = toast.loading("Suche gelöschte Anhänge...");
    try {
      const res = await axios.post(`${API}/azav/restore-deleted-attachments`, { dry_run: true });
      setRestoreScanResult(res.data);
      toast.success(`${res.data.anzahl_gefunden} gelöschte Anhänge gefunden`, { id: t });
    } catch (e) {
      toast.error("Scan fehlgeschlagen", { id: t });
    }
  }, []);

  const restoreCategory = useCallback(async (kat) => {
    if (!window.confirm(`Alle soft-gelöschten Anhänge der Kategorie "${kat}" wiederherstellen?`)) return;
    setRestoringCat(kat);
    const t = toast.loading(`Stelle ${kat}-Anhänge wieder her...`);
    try {
      const res = await axios.post(`${API}/azav/restore-deleted-attachments`, {
        dry_run: false, kategorie: kat,
      });
      toast.success(`${res.data.anzahl_wiederhergestellt} ${kat}-Anhänge wiederhergestellt!`, { id: t, duration: 8000 });
      await scanDeleted();
    } catch (e) {
      toast.error("Wiederherstellung fehlgeschlagen: " + (e.response?.data?.detail || e.message), { id: t });
    } finally {
      setRestoringCat(null);
    }
  }, [scanDeleted]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="anhang-repair-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#4A463D]">
            <Wrench size={22} /> Anhang-Reparatur
          </DialogTitle>
          <DialogDescription>
            Verschiebt alte Inline-Anhänge und Sonderfeld-Anhänge (Bildungsgutschein,
            Führungszeugnis, Praktikumsvertrag, Stundenplan) in die saubere
            <code className="px-1 py-0.5 bg-stone-100 rounded text-xs mx-1">teilnehmer_anhaenge</code>-Collection,
            korrigiert Kategorien per Heuristik und behebt die „Sonstige 1.1.1970"-Anzeige.
            Idempotent — kann jederzeit erneut ausgeführt werden.
          </DialogDescription>
        </DialogHeader>

        {/* Schritt 1: Scan */}
        <div className="rounded-lg border bg-amber-50 border-amber-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <FileWarning className="text-amber-700 mt-0.5" size={20} />
              <div>
                <div className="font-semibold text-amber-900">Schritt 1: Trockenlauf (sicher)</div>
                <div className="text-sm text-amber-800">
                  Scannt alle Teilnehmer, zeigt was migriert würde — ändert NICHTS.
                </div>
              </div>
            </div>
            <Button
              onClick={scan}
              disabled={scanning}
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="anhang-repair-scan-btn"
            >
              {scanning ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
              Scan starten
            </Button>
          </div>
        </div>

        {/* NOTFALL: Gelöschte Anhänge wiederherstellen */}
        <div className="rounded-lg border bg-emerald-50 border-emerald-200 p-4 mt-3">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-emerald-700 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="font-semibold text-emerald-900">NOTFALL: Verlorene Anhänge wiederherstellen</div>
              <div className="text-sm text-emerald-800">
                Wenn Bildungsgutscheine oder andere Anhänge verschwunden sind, sind sie meist nur soft-gelöscht
                und können wiederhergestellt werden. Diese Aktion findet sie und stellt sie wieder her.
              </div>
              <div className="mt-3 flex flex-wrap gap-2 items-center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={scanDeleted}
                  className="border-emerald-400 text-emerald-800 hover:bg-emerald-100"
                  data-testid="scan-deleted-btn"
                >
                  <RefreshCw size={14} className="mr-1" />
                  Gelöschte Anhänge suchen
                </Button>
                {restoreScanResult && (
                  <span className="text-sm text-emerald-900">
                    <b>{restoreScanResult.anzahl_gefunden}</b> gelöschte Anhänge gefunden
                    {restoreScanResult.kategorien?.length > 0 && (
                      <> in Kategorien: <code>{restoreScanResult.kategorien.join(", ")}</code></>
                    )}
                  </span>
                )}
              </div>
              {restoreScanResult && restoreScanResult.kategorien?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {restoreScanResult.kategorien.map((kat) => {
                    const count = restoreScanResult.beispiele.filter(e => e.kategorie === kat).length;
                    const moreThanShown = restoreScanResult.anzahl_gefunden > restoreScanResult.beispiele.length;
                    return (
                      <Button
                        key={kat}
                        size="sm"
                        onClick={() => restoreCategory(kat)}
                        disabled={restoringCat === kat}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white"
                        data-testid={`restore-${kat}-btn`}
                      >
                        {restoringCat === kat
                          ? <Loader2 className="animate-spin mr-1" size={14} />
                          : <CheckCircle2 size={14} className="mr-1" />}
                        {kat} wiederherstellen ({count}{moreThanShown ? "+" : ""})
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Ergebnis */}
        {dryRunResult && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4">
              <div className="rounded-lg border p-3 bg-white">
                <div className="text-xs text-stone-500">TN gescannt</div>
                <div className="text-2xl font-bold text-stone-800" data-testid="stat-tn-scanned">{stats.tn_gescannt}</div>
              </div>
              <div className="rounded-lg border p-3 bg-blue-50 border-blue-200">
                <div className="text-xs text-blue-700">Inline-Anhänge</div>
                <div className="text-2xl font-bold text-blue-800">{stats.inline_anhaenge_gefunden}</div>
                <div className="text-xs text-blue-600">{stats.inline_dedupliziert} bereits drin</div>
              </div>
              <div className="rounded-lg border p-3 bg-violet-50 border-violet-200">
                <div className="text-xs text-violet-700">Sonderfeld (BG/FZ/PV/SP)</div>
                <div className="text-2xl font-bold text-violet-800">{stats.pseudo_anhaenge_gefunden}</div>
                <div className="text-xs text-violet-600">{stats.pseudo_dedupliziert} bereits drin</div>
              </div>
              <div className="rounded-lg border p-3 bg-emerald-50 border-emerald-200">
                <div className="text-xs text-emerald-700">Zu migrieren</div>
                <div className="text-2xl font-bold text-emerald-800" data-testid="stat-will-migrate">{willMigrate}</div>
                <div className="text-xs text-emerald-600">+{stats.kategorie_korrigiert} Kat.-Korrekturen</div>
              </div>
            </div>

            {/* Aktionsliste */}
            {migrateAktionen.length > 0 ? (
              <div className="rounded-lg border overflow-hidden mb-4">
                <div className="bg-stone-50 px-3 py-2 text-sm font-semibold text-stone-700">
                  Geplante Migrationen ({migrateAktionen.length})
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-44">Teilnehmer</TableHead>
                        <TableHead className="w-32">Kategorie</TableHead>
                        <TableHead>Dateiname</TableHead>
                        <TableHead className="w-32">Quelle</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {migrateAktionen.slice(0, 100).map((a, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">{a.tn}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="font-mono text-[10px]">
                              {a.kategorie} — {KAT_LABEL[a.kategorie] || a.kategorie}
                            </Badge>
                            {a.kat_korrektur && (
                              <div className="text-[10px] text-amber-700 mt-0.5">
                                {a.kat_korrektur}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-xs truncate max-w-md" title={a.filename}>
                            {a.filename}
                          </TableCell>
                          <TableCell className="text-xs text-stone-500">
                            {a.typ === "pseudo_migrate" ? `Feld: ${a.feld}` : "Inline"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {migrateAktionen.length > 100 && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-xs text-center text-stone-500">
                            ... und {migrateAktionen.length - 100} weitere
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 mb-4 flex items-center gap-3">
                <CheckCircle2 className="text-emerald-600" />
                <div className="text-sm text-emerald-900">
                  <b>Alles sauber!</b> Keine Inline- oder Sonderfeld-Anhänge zu migrieren.
                </div>
              </div>
            )}

            {/* Migration-Action */}
            {willMigrate > 0 && (
              <div className="rounded-lg border bg-rose-50 border-rose-200 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-rose-700 mt-0.5" size={20} />
                  <div>
                    <div className="font-semibold text-rose-900">Schritt 2: Migration ausführen</div>
                    <div className="text-sm text-rose-800">
                      Anhänge werden in die saubere Collection kopiert. Die Original-Daten im TN-Dokument
                      bleiben standardmäßig erhalten (für Rollback).
                    </div>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer pl-7" data-testid="anhang-repair-delete-inline-toggle">
                  <Checkbox
                    checked={deleteInlineAfter}
                    onCheckedChange={setDeleteInlineAfter}
                  />
                  <span>
                    <b>Aufräumen:</b> Inline-Array (<code className="px-1 bg-rose-100 rounded text-xs">teilnehmer.anhaenge</code>) nach erfolgreicher Migration leeren.
                    <span className="text-rose-700"> (Sonderfelder bleiben unangetastet)</span>
                  </span>
                </label>
                <Button
                  onClick={migrate}
                  disabled={migrating}
                  className="bg-rose-700 hover:bg-rose-800 text-white"
                  data-testid="anhang-repair-migrate-btn"
                >
                  {migrating ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wrench size={16} className="mr-2" />}
                  {willMigrate} Anhänge jetzt migrieren
                </Button>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnhangRepair;
